import {
  AppstoreOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  FolderOpenOutlined,
  MoreOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  UnorderedListOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import {
  Button,
  Checkbox,
  DatePicker,
  Dropdown,
  Input,
  Pagination,
  Segmented,
  Select,
  Skeleton,
  Table,
  Tag,
  Tooltip,
} from "antd";
import type { TableColumnsType } from "antd";
import dayjs from "dayjs";
import { useState } from "react";

import type {
  MetadataItem,
  ProjectListItem,
  ProjectReviewAssignmentStatus,
  ProjectStatus,
} from "../../api/projects";
import type { UserListItem } from "../../api/users";
import { ProjectActionsMenu } from "./ProjectActionsMenu";
import { ProjectCard } from "./ProjectCard";
import { ProjectFolderIcon } from "./ProjectFolderIcon";

const STATUS_OPTIONS: Array<{ value: ProjectStatus; label: string }> = [
  { value: "SUBMITTED", label: "已提交" },
  { value: "COMPLETED", label: "已完成" },
];
const PROJECT_STATUS_LABELS = Object.fromEntries(
  STATUS_OPTIONS.map((item) => [item.value, item.label]),
) as Record<ProjectStatus, string>;

const REVIEW_ASSIGNMENT_OPTIONS: Array<{
  value: ProjectReviewAssignmentStatus;
  label: string;
}> = [
  { value: "ASSIGNED", label: "已分配审核员" },
  { value: "UNASSIGNED", label: "未分配审核员" },
];

interface ProjectLibraryFilters {
  searchInput: string;
  status?: ProjectStatus;
  workType?: string;
  ownerUserId?: string;
  reviewAssignmentStatus?: ProjectReviewAssignmentStatus;
  submittedDate: string;
}

interface ProjectLibraryCounts {
  all: number;
  submitted: number;
  completed: number;
}

interface ProjectLibraryViewProps {
  isAdmin: boolean;
  recycleBin: boolean;
  loading: boolean;
  projects: ProjectListItem[];
  total: number;
  page: number;
  counts: ProjectLibraryCounts;
  filters: ProjectLibraryFilters;
  workTypes: MetadataItem[];
  workTypeLabels: Record<string, string>;
  owners: UserListItem[];
  selectedProjectIds: string[];
  batchCompleting: boolean;
  batchDeleting: boolean;
  onSearchInputChange: (value: string) => void;
  onApplySearch: () => void;
  onResetFilters: () => void;
  onStatusChange: (value?: ProjectStatus) => void;
  onWorkTypeChange: (value?: string) => void;
  onOwnerChange: (value?: string) => void;
  onReviewAssignmentStatusChange: (value?: ProjectReviewAssignmentStatus) => void;
  onSubmittedDateChange: (value: string) => void;
  onToggleRecycleBin: () => void;
  onToggleCurrentPage: (checked: boolean) => void;
  onOpenAssignment: () => void;
  onBatchComplete: () => void;
  onBatchDelete: () => void;
  onSelectedProjectIdsChange: (projectIds: string[]) => void;
  onPageChange: (page: number) => void;
  onCreate: () => void;
  onSelectProject: (projectId: string, selected: boolean) => void;
  onView: (project: ProjectListItem) => void;
  onEdit: (project: ProjectListItem) => void;
  onChangeStatus: (project: ProjectListItem, status: ProjectStatus) => void;
  onDelete: (project: ProjectListItem) => void;
  onRestore: (project: ProjectListItem) => void;
}

export function ProjectLibraryView({
  isAdmin,
  recycleBin,
  loading,
  projects,
  total,
  page,
  counts,
  filters,
  workTypes,
  workTypeLabels,
  owners,
  selectedProjectIds,
  batchCompleting,
  batchDeleting,
  onSearchInputChange,
  onApplySearch,
  onResetFilters,
  onStatusChange,
  onWorkTypeChange,
  onOwnerChange,
  onReviewAssignmentStatusChange,
  onSubmittedDateChange,
  onToggleRecycleBin,
  onToggleCurrentPage,
  onOpenAssignment,
  onBatchComplete,
  onBatchDelete,
  onSelectedProjectIdsChange,
  onPageChange,
  onCreate,
  onSelectProject,
  onView,
  onEdit,
  onChangeStatus,
  onDelete,
  onRestore,
}: ProjectLibraryViewProps) {
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const visibleIds = projects.map((project) => project.id);
  const selectedVisibleCount = visibleIds.filter((id) => selectedProjectIds.includes(id)).length;
  const allVisibleSelected = visibleIds.length > 0 && selectedVisibleCount === visibleIds.length;
  const partiallyVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;
  const hasFilters = Boolean(
    filters.searchInput ||
    filters.status ||
    filters.workType ||
    filters.ownerUserId ||
    filters.reviewAssignmentStatus ||
    filters.submittedDate,
  );
  const listColumns: TableColumnsType<ProjectListItem> = [
    {
      title: "作品信息",
      key: "work",
      width: 310,
      render: (_, project) => (
        <span className="project-list-work">
          <ProjectFolderIcon className="project-list-folder" />
          <span>
            <Tooltip title={project.work_name}>
              <button type="button" onClick={() => onView(project)}>
                {project.work_name}
              </button>
            </Tooltip>
            <small>{project.project_number}</small>
          </span>
        </span>
      ),
    },
    ...(isAdmin
      ? [
          {
            title: "项目所属用户",
            dataIndex: "owner_name",
            width: 150,
            ellipsis: true,
          },
        ]
      : []),
    {
      title: "作品类型",
      dataIndex: "work_type_code",
      width: 150,
      ellipsis: true,
      render: (value: string) => workTypeLabels[value] ?? value,
    },
    {
      title: "项目标记",
      dataIndex: "status",
      width: 110,
      render: (value: ProjectStatus) => (
        <Tag
          className={`project-card-status--${value.toLowerCase()}`}
          variant="filled"
        >
          {PROJECT_STATUS_LABELS[value]}
        </Tag>
      ),
    },
    {
      title: "更新时间",
      dataIndex: "updated_at",
      width: 130,
      render: (value: string) => value.slice(0, 10),
    },
    {
      title: "操作",
      key: "actions",
      width: 78,
      align: "center",
      render: (_, project) => (
        <ProjectActionsMenu
          project={project}
          isAdmin={isAdmin}
          recycleBin={recycleBin}
          className="project-list-menu"
          onView={onView}
          onEdit={onEdit}
          onChangeStatus={onChangeStatus}
          onDelete={onDelete}
          onRestore={onRestore}
        />
      ),
    },
  ];

  return (
    <section className="project-library" aria-label="项目列表">
      <header className="project-library-header">
        <div className="project-library-title">
          <strong>{recycleBin ? "项目回收站" : "项目列表"}</strong>
          <span>共 {counts.all} 个项目</span>
        </div>
        <div className="project-status-segments" aria-label="按项目标签筛选">
          <button
            className={!filters.status ? "is-active" : ""}
            type="button"
            onClick={() => onStatusChange()}
          >
            全部 <b>{counts.all}</b>
          </button>
          <button
            className={`is-submitted${filters.status === "SUBMITTED" ? " is-active" : ""}`}
            type="button"
            onClick={() => onStatusChange("SUBMITTED")}
          >
            已提交 <b>{counts.submitted}</b>
          </button>
          <button
            className={`is-completed${filters.status === "COMPLETED" ? " is-active" : ""}`}
            type="button"
            onClick={() => onStatusChange("COMPLETED")}
          >
            已完成 <b>{counts.completed}</b>
          </button>
        </div>
      </header>

      <div className="project-filter-row">
        <Segmented
          className="project-view-switch"
          aria-label="项目列表显示方式"
          value={viewMode}
          options={[
            { value: "grid", label: "网格", icon: <AppstoreOutlined /> },
            { value: "list", label: "列表", icon: <UnorderedListOutlined /> },
          ]}
          onChange={(value) => setViewMode(value as "grid" | "list")}
        />
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="搜索作品名称或项目编号"
          value={filters.searchInput}
          onChange={(event) => onSearchInputChange(event.target.value)}
          onPressEnter={onApplySearch}
        />
        <Select<string>
          allowClear
          placeholder="作品类型"
          value={filters.workType}
          options={workTypes.map((item) => ({ value: item.code, label: item.label }))}
          onChange={onWorkTypeChange}
        />
        <Select<ProjectStatus>
          allowClear
          placeholder="项目标签"
          value={filters.status}
          options={STATUS_OPTIONS}
          onChange={onStatusChange}
        />
        <DatePicker
          className="project-date-filter"
          allowClear
          placeholder="提交时间"
          format="YYYY-MM-DD"
          value={filters.submittedDate ? dayjs(filters.submittedDate) : null}
          onChange={(value) => onSubmittedDateChange(value?.format("YYYY-MM-DD") ?? "")}
        />
        <Button onClick={onResetFilters}>重置</Button>
        <Button type="primary" onClick={onApplySearch}>
          查询
        </Button>
      </div>

      {isAdmin ? (
        <div className="project-admin-bar">
          <div className="project-admin-filters">
            <div className="project-owner-filter">
              <span>项目所属用户</span>
              <Select<string>
                aria-label="项目所属用户"
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="全部普通用户"
                value={filters.ownerUserId}
                options={owners.map((item) => ({
                  value: item.id,
                  label: `${item.name}（${item.username}）`,
                }))}
                onChange={onOwnerChange}
              />
            </div>
            <div className="project-owner-filter">
              <span>审核分配状态</span>
              <Select<ProjectReviewAssignmentStatus>
                aria-label="审核分配状态"
                allowClear
                placeholder="全部分配状态"
                value={filters.reviewAssignmentStatus}
                options={REVIEW_ASSIGNMENT_OPTIONS}
                onChange={onReviewAssignmentStatusChange}
              />
            </div>
          </div>
          <div className="project-batch-actions">
            {!recycleBin ? (
              <>
                <Checkbox
                  checked={allVisibleSelected}
                  indeterminate={partiallyVisibleSelected}
                  onChange={(event) => onToggleCurrentPage(event.target.checked)}
                >
                  全选本页
                </Checkbox>
                <span>已选择 {selectedProjectIds.length} 个项目</span>
                <Dropdown
                  trigger={["click"]}
                  menu={{
                    items: [
                      {
                        key: "assign",
                        label: "添加审核员",
                        icon: <UserAddOutlined />,
                      },
                      {
                        key: "complete",
                        label: "批量标记完成",
                        icon: <CheckCircleOutlined />,
                        disabled: batchDeleting,
                      },
                      {
                        key: "delete",
                        label: "批量删除",
                        icon: <DeleteOutlined />,
                        danger: true,
                        disabled: batchCompleting,
                      },
                    ],
                    onClick: ({ key }) => {
                      if (key === "assign") onOpenAssignment();
                      if (key === "complete") onBatchComplete();
                      if (key === "delete") onBatchDelete();
                    },
                  }}
                >
                  <Button
                    type="primary"
                    icon={<MoreOutlined />}
                    disabled={!selectedProjectIds.length}
                    loading={batchCompleting || batchDeleting}
                  >
                    批量操作
                  </Button>
                </Dropdown>
              </>
            ) : null}
            <Button
              type={recycleBin ? "primary" : "default"}
              danger={recycleBin}
              icon={recycleBin ? <ReloadOutlined /> : <DeleteOutlined />}
              onClick={onToggleRecycleBin}
            >
              {recycleBin ? "返回全部项目" : "项目回收站"}
            </Button>
          </div>
        </div>
      ) : null}

      <div className="project-library-content">
        {viewMode === "list" ? (
          <Table<ProjectListItem>
            className="project-list-table"
            rowKey="id"
            size="middle"
            loading={loading}
            dataSource={projects}
            columns={listColumns}
            pagination={false}
            scroll={{ x: 900 }}
            locale={{ emptyText: hasFilters ? "没有匹配的项目" : "暂无项目" }}
            rowSelection={
              isAdmin && !recycleBin
                ? {
                    selectedRowKeys: selectedProjectIds,
                    onChange: (keys) => onSelectedProjectIdsChange(keys.map(String)),
                  }
                : undefined
            }
          />
        ) : loading ? (
          <div className="project-grid" aria-label="项目加载中">
            {Array.from({ length: 10 }, (_, index) => (
              <div className="project-card-skeleton" key={index}>
                <Skeleton active paragraph={{ rows: 3 }} title={false} />
              </div>
            ))}
          </div>
        ) : projects.length ? (
          <div className="project-grid">
            {projects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                workTypeLabel={workTypeLabels[project.work_type_code] ?? project.work_type_code}
                isAdmin={isAdmin}
                recycleBin={recycleBin}
                selected={selectedProjectIds.includes(project.id)}
                onSelect={onSelectProject}
                onView={onView}
                onEdit={onEdit}
                onChangeStatus={onChangeStatus}
                onDelete={onDelete}
                onRestore={onRestore}
              />
            ))}
          </div>
        ) : (
          <div className="project-empty-state">
            <div className="project-empty-illustration" aria-hidden="true">
              <FolderOpenOutlined />
            </div>
            <strong>{hasFilters ? "没有匹配的项目" : "暂无项目"}</strong>
            <p>
              {isAdmin
                ? "当前范围内还没有项目，请调整查询条件后重试。"
                : "创建您的第一个版权登记项目，开始提交和管理作品材料。"}
            </p>
            {!isAdmin ? (
              <Button type="primary" icon={<PlusOutlined />} onClick={onCreate}>
                提交登记
              </Button>
            ) : null}
          </div>
        )}
      </div>

      <footer className="project-pagination">
        <span>共 {total} 个项目</span>
        <Pagination
          current={page}
          pageSize={10}
          total={total}
          showSizeChanger={false}
          hideOnSinglePage={total <= 10}
          onChange={onPageChange}
        />
      </footer>
    </section>
  );
}
